import {
  Bell,
  Check,
  CheckCheck,
  Loader2,
  Trash2,
} from "@ews-admin/global-design-system";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
  deleteNotificationRequest,
  fetchNotificationsRequest,
  markAllNotificationsReadRequest,
  markNotificationReadRequest,
  selectNotifications,
  selectNotificationsError,
  selectNotificationsLoaded,
  selectNotificationsLoading,
  selectUnreadNotificationCount,
} from "../../store/reducers/notificationSlice";
import { formatTimestamp } from "../../utils/date-format-utils";

/**
 * MED-1447: the header bell, backed by the four in-app notification
 * operations `medecine360-bff` exposes — list, mark one read, mark all read,
 * delete one. Nothing here is hardcoded: the panel renders whatever
 * `GET /api/v1/notifications` returns, and an empty response renders the
 * empty state, which is what the removed demo panel faked.
 */
export const NotificationsPanel: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const notifications = useAppSelector(selectNotifications);
  const unreadCount = useAppSelector(selectUnreadNotificationCount);
  const loading = useAppSelector(selectNotificationsLoading);
  const loaded = useAppSelector(selectNotificationsLoaded);
  const error = useAppSelector(selectNotificationsError);

  const [isOpen, setIsOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  // Fetched on mount, not on open: the badge has to be right before the
  // administrator clicks anything.
  useEffect(() => {
    dispatch(fetchNotificationsRequest());
  }, [dispatch]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        panelRef.current &&
        !panelRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const showSpinner = loading && !loaded;
  const showEmptyState = !showSpinner && !error && notifications.length === 0;

  return (
    <div className="relative" ref={panelRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        aria-label={t("admin.notifications.open")}
        aria-expanded={isOpen}
        className="relative p-2 rounded-lg transition-colors hover:bg-neutral-100"
      >
        <Bell className="w-5 h-5 text-neutral-600" />
        {unreadCount > 0 && (
          <span className="flex absolute -top-0.5 -right-0.5 justify-center items-center px-1 min-w-[1.125rem] h-[1.125rem] text-[0.625rem] font-semibold text-white rounded-full bg-error-600">
            {unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 z-50 mt-2 w-80 bg-white rounded-xl border shadow-soft border-neutral-200">
          <div className="flex justify-between items-center px-4 py-3 border-b border-neutral-200">
            <div>
              <p className="text-sm font-semibold text-neutral-900">
                {t("admin.notifications.title")}
              </p>
              <p className="text-xs text-neutral-500">
                {t("admin.notifications.unreadCount", { count: unreadCount })}
              </p>
            </div>
            {unreadCount > 0 && (
              <button
                type="button"
                onClick={() => dispatch(markAllNotificationsReadRequest())}
                className="flex items-center px-2 py-1 space-x-1 text-xs rounded-lg transition-colors text-ews-primary hover:bg-neutral-100"
              >
                <CheckCheck className="w-3.5 h-3.5" />
                <span>{t("admin.notifications.markAllRead")}</span>
              </button>
            )}
          </div>

          <div className="overflow-y-auto max-h-80">
            {showSpinner && (
              <div className="flex justify-center items-center px-4 py-8 space-x-2 text-sm text-neutral-500">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>{t("admin.notifications.loading")}</span>
              </div>
            )}

            {error && (
              <div className="px-4 py-6 text-center">
                <p className="text-sm text-error-600">
                  {t("admin.notifications.loadError")}
                </p>
                <button
                  type="button"
                  onClick={() => dispatch(fetchNotificationsRequest())}
                  className="mt-2 text-xs underline text-ews-primary"
                >
                  {t("common.retry")}
                </button>
              </div>
            )}

            {showEmptyState && (
              <div className="px-4 py-8 text-center">
                <p className="text-sm font-medium text-neutral-700">
                  {t("admin.notifications.empty")}
                </p>
                <p className="mt-1 text-xs text-neutral-500">
                  {t("admin.notifications.emptyHint")}
                </p>
              </div>
            )}

            {notifications.map(notification => (
              <div
                key={notification.id}
                className={`px-4 py-3 border-b last:border-b-0 border-neutral-100 ${
                  notification.isRead ? "bg-white" : "bg-neutral-50"
                }`}
              >
                <p className="text-sm font-medium text-neutral-900">
                  {notification.title}
                </p>
                {notification.message && (
                  <p className="mt-0.5 text-xs text-neutral-600">
                    {notification.message}
                  </p>
                )}
                <div className="flex justify-between items-center mt-2">
                  <span className="text-[0.625rem] text-neutral-400">
                    {formatTimestamp(notification.createdAt)}
                  </span>
                  <div className="flex items-center space-x-1">
                    {!notification.isRead && (
                      <button
                        type="button"
                        onClick={() =>
                          dispatch(
                            markNotificationReadRequest({
                              id: notification.id,
                            })
                          )
                        }
                        aria-label={`${t("admin.notifications.markRead")} — ${
                          notification.title
                        }`}
                        className="p-1 rounded transition-colors text-neutral-500 hover:bg-neutral-100 hover:text-neutral-700"
                      >
                        <Check className="w-3.5 h-3.5" />
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() =>
                        dispatch(
                          deleteNotificationRequest({ id: notification.id })
                        )
                      }
                      aria-label={`${t("common.delete")} — ${
                        notification.title
                      }`}
                      className="p-1 rounded transition-colors text-neutral-500 hover:bg-error-50 hover:text-error-600"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
